import React, { useMemo } from 'react'
import { Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import useConfig from '~/prix/react/hooks/config'
import ItineraryReportHeaderPdf from './ItineraryReportHeaderPdf.component'
import ItineraryReportFooterPdf from './ItineraryReportFooterPdf.component'
import { ItineraryReportPdfProps, ItineraryStats, CompanyFeature } from './ItineraryReportPdf.types'
import { sizeColors } from '~/design'
import { getCharFromAlphabet } from '~/prix/utils/types/string'
import { maskCpf } from '~/prix/utils/cpf'

const styles = StyleSheet.create({
  page: {
    paddingTop: 20,
    paddingBottom: 65,
    paddingHorizontal: 40,
  },

  title: {
    fontSize: 20,
    color: '#0F438A',
    fontFamily: 'Lato-700',
    marginBottom: 8,
  },

  subtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 15,
  },

  summaryGrid: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    marginBottom: 15,
  },

  summaryColumn: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 10,
  },

  summaryCard: {
    backgroundColor: '#F5F5F5',
    borderRadius: 6,
    padding: 12,
  },

  summaryLabel: {
    fontSize: 11,
    color: '#666',
    marginBottom: 4,
  },

  summaryValue: {
    fontSize: 18,
    color: '#0F438A',
    fontFamily: 'Lato-700',
  },

  mapSection: {
    marginTop: 10,
  },

  mapTitle: {
    fontSize: 14,
    color: '#0F438A',
    fontFamily: 'Lato-700',
    marginBottom: 10,
  },

  mapContainer: {
    display: 'flex',
    height: 280,
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #E0E0E0',
  },

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  mapLegend: {
    marginTop: 8,
    padding: 8,
    backgroundColor: '#F9F9F9',
    borderRadius: 4,
  },

  legendText: {
    fontSize: 9,
    color: '#666',
    lineHeight: 1.4,
  },

  metadata: {
    marginTop: 20,
    fontSize: 10,
    color: '#999',
    textAlign: 'center',
  },
})

function calculateStats(itinerary: ItineraryReportPdfProps['itinerary']): ItineraryStats {
  const companies = itinerary.features.filter(
    (f: CompanyFeature) => f.properties?.kind === 'interestPoint',
  )

  const totalDistance = companies.reduce(
    (sum: number, f: CompanyFeature) =>
      sum + (Number(f.properties?.intersectionLengthKilometers) || 0),
    0,
  )

  const totalDuration = companies.reduce(
    (sum: number, f: CompanyFeature) => sum + (Number(f.properties?.duration) || 0),
    0,
  )

  const sizeBreakdown = companies.reduce((acc: Record<string, number>, f: CompanyFeature) => {
    const size = f.properties?.size || 'Não informado'
    acc[size] = (acc[size] || 0) + 1
    return acc
  }, {})

  const segmentBreakdown = companies.reduce((acc: Record<string, number>, f: CompanyFeature) => {
    const segment = f.properties?.segmentDescription || 'Não informado'
    acc[segment] = (acc[segment] || 0) + 1
    return acc
  }, {})

  const cnaeBreakdown = companies.reduce((acc: Record<string, number>, f: CompanyFeature) => {
    const cnae = f.properties?.mainCnaeDescription || 'Não informado'
    acc[cnae] = (acc[cnae] || 0) + 1
    return acc
  }, {})

  return {
    totalCompanies: companies.length,
    totalDistance,
    estimatedDuration: totalDuration,
    sizeBreakdown,
    segmentBreakdown,
    cnaeBreakdown,
  }
}

export default function ItineraryReportCoverPdf({
  itinerary,
  attendant,
  space,
  generatedAt = new Date(),
}: ItineraryReportPdfProps) {
  const config = useConfig()
  const apiKey = config.services.googleConfig.apiKey

  const stats = useMemo(() => calculateStats(itinerary), [itinerary])

  const spaceLabel = useMemo(() => {
    const typeMap: Record<string, string> = {
      state: 'Estado',
      city: 'Município',
      neighborhood: 'Bairro',
      region: 'Região',
    }
    return typeMap[space.delimitationType] || 'Área'
  }, [space.delimitationType])

  const formattedDate = format(generatedAt, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
  const formattedTime = format(generatedAt, 'HH:mm', { locale: ptBR })

  const hours = Math.floor(stats.estimatedDuration / 60)
  const minutes = stats.estimatedDuration % 60
  const durationLabel = hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`

  const mapUrl = useMemo(() => {
    const companies = itinerary.features.filter(
      (f: CompanyFeature) => f.properties?.kind === 'interestPoint',
    )

    const markers = companies
      .map((f: CompanyFeature, index: number) => {
        const markerColor = (
          sizeColors[f.properties?.size as keyof typeof sizeColors] || sizeColors['Others']
        ).replace('#', '0x')
        const coords = (f.geometry as any).coordinates

        const position = Number(f.properties?.position)
        return `markers=color:${markerColor}|label:${getCharFromAlphabet(position)}|${coords[1]},${coords[0]}`
      })
      .join('&')

    const paths = itinerary.features
      .filter((f: any) => f.geometry?.type === 'LineString')
      .map((f: any) => {
        const coords = f.geometry.coordinates
        const pathString = coords.map((c: number[]) => `${c[1]},${c[0]}`).join('|')
        return `path=color:0x005EB8FF|weight:4|${pathString}`
      })
      .join('&')

    const visiblePoints = companies
      .map((f: CompanyFeature) => {
        const coords = (f.geometry as any).coordinates
        return `${coords[1]},${coords[0]}`
      })
      .join('|')

    const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap'
    const params = [
      `visible=${visiblePoints}`,
      'size=640x400',
      'scale=2', // Aumentar a resolução do mapa
      'maptype=roadmap',
      markers,
      paths,
      `key=${apiKey}`,
    ].join('&')

    return `${baseUrl}?${params}`
  }, [itinerary, apiKey])

  return (
    <Page size='A4' style={styles.page}>
      <ItineraryReportHeaderPdf />

      {attendant && (
        <>
          <Text style={styles.title}>Agente</Text>

          <View style={styles.summaryGrid}>
            <View style={{ flex: 1 }}>
              <Text style={styles.summaryLabel}>Nome:</Text>
              <Text style={styles.mapTitle}>{attendant?.attendantName || 'Não informado'}</Text>
            </View>

            <View style={{ flex: 1 }}>
              <Text style={styles.summaryLabel}>CPF:</Text>
              <Text style={styles.mapTitle}>
                {maskCpf(attendant?.attendantCpf) || 'Não informado'}
              </Text>
            </View>
          </View>
        </>
      )}

      <Text style={styles.title}>Roteiro de Atendimento</Text>
      {/* <Text style={styles.subtitle}>{spaceLabel} - {space.delimitationTitle}</Text> */}

      {/* Resumo do itinerário */}
      <View style={styles.summaryGrid}>
        {/* Coluna esquerda */}
        <View style={styles.summaryColumn}>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>Variedade de portes</Text>
            <Text style={styles.summaryValue}>
              {Object.keys(stats.sizeBreakdown).length} diferentes
            </Text>
          </View>

          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>Quantidade de empresas</Text>
            <Text style={styles.summaryValue}>{stats.totalCompanies}</Text>
          </View>

          {/* <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>Distância percorrida</Text>
            <Text style={styles.summaryValue}>{stats.totalDistance.toFixed(1)} km</Text>
          </View>

          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>Duração de travessia</Text>
            <Text style={styles.summaryValue}>{durationLabel}</Text>
          </View> */}
        </View>

        {/* Coluna direita */}
        <View style={styles.summaryColumn}>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>Variedade de segmentos</Text>
            <Text style={styles.summaryValue}>
              {Object.keys(stats.segmentBreakdown).length} diferentes
            </Text>
          </View>

          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>Variedade de CNAEs</Text>
            <Text style={styles.summaryValue}>
              {Object.keys(stats.cnaeBreakdown).length} diferentes
            </Text>
          </View>
        </View>
      </View>

      {/* Map Section */}
      <View style={styles.mapSection}>
        <Text style={styles.mapTitle}>Visualização da Rota</Text>
        <View style={styles.mapContainer}>
          <Image src={mapUrl} style={styles.image} />
        </View>
        <View style={styles.mapLegend}>
          <Text style={styles.legendText}>• Marcadores definem a posição da empresa</Text>
          <Text style={styles.legendText}>• Linha azul mostra a rota sugerida</Text>
        </View>
      </View>

      <ItineraryReportFooterPdf generatedAt={generatedAt} />
    </Page>
  )
}
